/**
 * BulkDeleteContractsDialog — Confirmation de suppression multiple avec
 * impact preview (factures, dossiers, documents liés par contrat).
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle, Loader2, FileText, FolderOpen, Receipt, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => (v == null ? "-" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v));

export default function BulkDeleteContractsDialog({ open, ids, onOpenChange, onDone }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open || !ids?.length) return;
    setLoading(true);
    setImpact(null);
    setReason("");
    api.post("/contracts/bulk-deletion-impact", { ids })
      .then((r) => setImpact(r.data))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [open, ids]);

  const totalInvoices = (impact?.items || []).reduce((a, x) => a + (x.invoices_count || 0), 0);
  const totalFolders = (impact?.items || []).reduce((a, x) => a + (x.folders_count || 0), 0);
  const totalDocs = (impact?.items || []).reduce((a, x) => a + (x.documents_count || 0), 0);
  const totalAmount = (impact?.items || []).reduce((a, x) => a + (x.amount_ht || 0), 0);

  const confirm = async () => {
    setDeleting(true);
    try {
      const r = await api.post("/contracts/bulk-delete", { ids, reason: reason.trim() || null });
      toast.success(`${r.data.deleted} contrat(s) supprimé(s)`
        + (r.data.skipped?.length ? ` · ${r.data.skipped.length} ignoré(s)` : ""));
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Suppression multiple de {ids?.length || 0} contrat{(ids?.length || 0) > 1 ? "s" : ""}
          </DialogTitle>
          <DialogDescription>
            Cette action est réversible via la corbeille tant que la purge définitive n'a pas eu lieu.
            Les éléments liés (factures, dossiers, documents) sont conservés et redeviennent
            accessibles en cas de restauration.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" />
          </div>
        ) : impact ? (
          <div className="space-y-4">
            {/* Résumé global */}
            <div className="grid grid-cols-4 gap-2">
              <SummaryCard icon={Trash2} label="Contrats" value={impact.count} tone="red" />
              <SummaryCard icon={Receipt} label="Factures liées" value={totalInvoices} tone="amber" />
              <SummaryCard icon={FolderOpen} label="Dossiers" value={totalFolders} tone="blue" />
              <SummaryCard icon={FileText} label="Documents" value={totalDocs} tone="slate" />
            </div>
            <div className="text-xs text-slate-600 text-right">
              Montant HT cumulé : <b className="font-mono">{fmtEUR(totalAmount)}</b>
            </div>

            {/* Détail par contrat */}
            <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                  <tr>
                    <th className="text-left p-2">Contrat</th>
                    <th className="text-right p-2">Fact.</th>
                    <th className="text-right p-2">Dossiers</th>
                    <th className="text-right p-2">Docs</th>
                    <th className="text-right p-2">HT</th>
                  </tr>
                </thead>
                <tbody>
                  {impact.items.map((x) => (
                    <tr key={x.id} className="border-t"
                        data-testid={`bulk-impact-row-${x.id}`}>
                      {x.found ? (
                        <>
                          <td className="p-2">
                            <div className="text-slate-900">{x.title}</div>
                            <div className="text-slate-500 text-[10px]">
                              {x.party_type === "supplier" ? "Fournisseur" : "Client"} · {x.societe}
                            </div>
                          </td>
                          <td className="p-2 text-right font-mono">{x.invoices_count}</td>
                          <td className="p-2 text-right font-mono">{x.folders_count}</td>
                          <td className="p-2 text-right font-mono">{x.documents_count}</td>
                          <td className="p-2 text-right font-mono">{fmtEUR(x.amount_ht)}</td>
                        </>
                      ) : (
                        <td colSpan={5} className="p-2 text-slate-400 italic">
                          Contrat introuvable ({x.id.slice(0, 8)}) — ignoré
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalInvoices > 0 && (
              <div className="text-[11px] rounded-md border border-amber-200 bg-amber-50 text-amber-900 p-2 flex items-start gap-1.5">
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  <b>{totalInvoices} facture{totalInvoices > 1 ? "s" : ""}</b> liée{totalInvoices > 1 ? "s" : ""} : les factures conservent leur lien mais le contrat associé sera marqué comme supprimé.
                </span>
              </div>
            )}

            {/* Raison de suppression (optionnelle) — visible dans l'audit trail */}
            <div className="space-y-1.5">
              <Label className="text-xs flex items-center gap-1.5 text-slate-700">
                <MessageSquare className="h-3 w-3" />
                Raison de la suppression <span className="text-slate-400">(facultatif — visible dans la corbeille et l'audit)</span>
              </Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value.slice(0, 500))}
                        placeholder="Ex : doublon avec CTR-2026-045, contrat résilié depuis 2024, erreur de saisie…"
                        rows={2}
                        className="text-xs resize-none"
                        data-testid="bulk-delete-reason-input" />
              <div className="text-[10px] text-slate-400 text-right">{reason.length}/500</div>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={deleting}
                  data-testid="bulk-delete-cancel">
            Annuler
          </Button>
          <Button onClick={confirm} disabled={deleting || loading || !impact}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="bulk-delete-confirm">
            {deleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                       : <Trash2 className="h-4 w-4 mr-1.5" />}
            {deleting ? "Suppression…" : `Supprimer ${ids?.length || 0} contrat${(ids?.length || 0) > 1 ? "s" : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryCard({ icon: Icon, label, value, tone }) {
  const cls = {
    red: "border-red-200 bg-red-50 text-red-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    slate: "border-slate-200 bg-slate-50 text-slate-800",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-800";
  return (
    <div className={`rounded-md border p-2 text-center ${cls}`}>
      <Icon className="h-3.5 w-3.5 mx-auto mb-1" />
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wide">{label}</div>
    </div>
  );
}
